import React, { lazy, Suspense, useMemo, useState } from 'react';
import { LuArrowRight, LuTimer } from 'react-icons/lu';
import { SearchInput, Select } from '../ui';
import {
  useCaseExecutions,
  useCaseOverview,
  useDailyExecutionTrend,
  useDebouncedUrlSearch,
  useEnvironmentExecutionReport,
  useExecutionSummary,
  usePlanExecutions,
  useSuiteExecutions,
  useTestHistory,
} from './reportHooks';
import {
  formatDate,
  formatDateTime,
  formatDuration,
  formatPercent,
  normalizeStatus,
  passRateTone,
  testKeyOf,
  toNumber,
} from './reportData';
import {
  DrawerActions,
  HealthPill,
  OutcomeBar,
  PassRateBar,
  PlanRunDetails,
  ReportDrawer,
  ReportEmptyState,
  ReportErrorState,
  ReportSection,
  ReportSkeleton,
  ReportTable,
  StatusPill,
  SuiteRunDetails,
  TestCaseDetails,
} from './ReportUI';

const TrendChart = lazy(() => import('./TrendChart'));

const VIEWS = [
  { id: 'cases', label: 'Test cases' },
  { id: 'runs', label: 'Suite runs' },
  { id: 'plans', label: 'Plan runs' },
  { id: 'environments', label: 'Environments' },
];

export const historyToCaseRow = (t) => {
  const latest = t.runs[0] || {};
  return {
    test_case_id: t.testCaseId,
    test_case_name: t.name,
    suite_name: t.suite,
    test_plan_name: t.plan,
    environment_name: latest.environment,
    status: latest.status,
    duration: latest.duration,
    executed_at: latest.executedAt,
    execution_id: latest.executionId,
  };
};

function CoverageRow({ label, bucket }) {
  const executed = toNumber(bucket?.executed);
  const total = toNumber(bucket?.total);
  const pct = total > 0 ? (executed / total) * 100 : null;
  return (
    <div className="rpt-coverage__row">
      <span className="rpt-coverage__label">{label}</span>
      <span className="rpt-coverage__track" aria-hidden>
        <span className="rpt-coverage__fill" style={{ width: `${pct ?? 0}%` }} />
      </span>
      <span className="rpt-coverage__value">
        {bucket ? `${executed} / ${total}` : '—'}
      </span>
    </div>
  );
}

function SuiteStatusBars({ rows }) {
  if (!rows.length) return <p className="rpt-empty-inline">No suite results in this period.</p>;
  const ordered = [...rows]
    .map((r) => ({
      name: r.suite_name || '—',
      passed: toNumber(r.passed),
      failed: toNumber(r.failed),
      pending: toNumber(r.pending),
    }))
    .sort((a, b) => b.failed - a.failed || b.passed + b.failed + b.pending - (a.passed + a.failed + a.pending))
    .slice(0, 8);
  const max = Math.max(1, ...ordered.map((r) => r.passed + r.failed + r.pending));
  return (
    <ul className="rpt-stackbars">
      {ordered.map((r) => {
        const total = r.passed + r.failed + r.pending;
        return (
          <li key={r.name} className="rpt-stackbars__row">
            <span className="rpt-stackbars__label" title={r.name}>{r.name}</span>
            <span
              className="rpt-stackbars__track"
              role="img"
              aria-label={`${r.name}: ${r.passed} passed, ${r.failed} failed, ${r.pending} pending`}
            >
              <span className="rpt-stackbars__inner" style={{ width: `${(total / max) * 100}%` }}>
                {r.passed > 0 && <span className="rpt-fill--success" style={{ flexGrow: r.passed }} title={`Passed: ${r.passed}`} />}
                {r.failed > 0 && <span className="rpt-fill--danger" style={{ flexGrow: r.failed }} title={`Failed: ${r.failed}`} />}
                {r.pending > 0 && <span className="rpt-fill--warning" style={{ flexGrow: r.pending }} title={`Pending: ${r.pending}`} />}
              </span>
            </span>
            <span className="rpt-stackbars__value">
              {r.failed > 0 ? <b className="rpt-text--danger">{r.failed} failed</b> : <span className="rpt-muted">{total} run</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function ChangeTiles({ history, onOpen }) {
  const tiles = [
    { key: 'new', label: 'New failures', value: history.newFailures, tone: 'danger' },
    { key: 'recurring', label: 'Recurring failures', value: history.recurring, tone: 'warning' },
    { key: 'recovered', label: 'Recovered', value: history.recovered, tone: 'success' },
    { key: 'changed', label: 'Changed status', value: history.changedStatus, tone: 'info' },
  ];
  return (
    <div className="rpt-tiles">
      {tiles.map((t) => (
        <button key={t.key} type="button" className={`rpt-tile rpt-tile--${t.tone}`} onClick={onOpen}>
          <span className="rpt-tile__value">{t.value}</span>
          <span className="rpt-tile__label">{t.label}</span>
        </button>
      ))}
    </div>
  );
}

export default function DetailedReport({ ctx, params, update, nav }) {
  const view = VIEWS.some((v) => v.id === params.view) ? params.view : 'cases';
  const tabCtx = (id) => ({ ...ctx, enabled: ctx.enabled && view === id });

  const execSummary = useExecutionSummary(ctx);
  const daily = useDailyExecutionTrend(ctx);
  const history = useTestHistory(ctx);
  const caseOverview = useCaseOverview(ctx);

  const cases = useCaseExecutions(tabCtx('cases'), {
    page: params.c_page,
    pageSize: params.c_size,
    search: params.c_q,
    status: params.c_status,
  });
  const runs = useSuiteExecutions(tabCtx('runs'), {
    page: params.r_page,
    pageSize: params.r_size,
    search: params.r_q,
    executionType: params.r_type,
  });
  const plans = usePlanExecutions(tabCtx('plans'), {
    page: params.p_page,
    pageSize: params.p_size,
    search: params.p_q,
  });
  const envs = useEnvironmentExecutionReport(tabCtx('environments'));

  const [caseQ, setCaseQ] = useDebouncedUrlSearch(params.c_q, (v) => update({ c_q: v }));
  const [runQ, setRunQ] = useDebouncedUrlSearch(params.r_q, (v) => update({ r_q: v }));
  const [planQ, setPlanQ] = useDebouncedUrlSearch(params.p_q, (v) => update({ p_q: v }));

  const [drawer, setDrawer] = useState(null);
  const closeDrawer = () => setDrawer(null);

  const results = execSummary.data?.results;
  const coverage = execSummary.data?.coverage;
  const health = execSummary.data?.health;

  const durationPoints = useMemo(() => {
    const rows = [...(daily.data?.rows || [])].filter((r) => r.date);
    rows.sort((a, b) => String(a.date).localeCompare(String(b.date)));
    return rows.map((r) => ({
      label: formatDate(r.date).replace(/ \d{4}$/, ''),
      value: r.avgDuration == null ? null : toNumber(r.avgDuration),
      tooltip: [`${r.totalExecuted} executed · ${r.failed} failed`],
    }));
  }, [daily.data]);
  const hasDuration = durationPoints.some((p) => p.value != null && p.value > 0);

  const h = history.data;

  const openCase = (row) => setDrawer({ kind: 'case', row });

  const caseColumns = [
    { key: 'test_case_name', header: 'Test case', sortable: true, render: (r) => <span className="rpt-cell-title">{r.test_case_name || '—'}</span> },
    { key: 'suite_name', header: 'Suite', sortable: true, render: (r) => r.suite_name || '—' },
    {
      key: 'test_plan_name',
      header: 'Test plan',
      render: (r) => r.test_plan_name || <span className="rpt-tag">Individual run</span>,
    },
    { key: 'environment_name', header: 'Environment', sortable: true, render: (r) => r.environment_name || '—' },
    { key: 'status', header: 'Status', sortable: true, sortValue: (r) => normalizeStatus(r.status), render: (r) => <StatusPill status={r.status} /> },
    {
      key: 'duration',
      header: 'Duration',
      sortable: true,
      align: 'right',
      sortValue: (r) => toNumber(r.duration, null),
      render: (r) => <span className="rpt-mono">{formatDuration(r.duration)}</span>,
    },
    { key: 'executed_at', header: 'Executed', sortable: true, render: (r) => formatDateTime(r.executed_at) },
  ];

  const runColumns = [
    { key: 'suite_name', header: 'Suite', sortable: true, render: (r) => <span className="rpt-cell-title">{r.suite_name || '—'}</span> },
    { key: 'execution_status', header: 'Status', sortable: true, sortValue: (r) => normalizeStatus(r.execution_status), render: (r) => <StatusPill status={r.execution_status} /> },
    { key: 'pass_rate', header: 'Pass rate', sortable: true, width: 160, sortValue: (r) => toNumber(r.pass_rate, null), render: (r) => <PassRateBar rate={r.pass_rate} compact /> },
    {
      key: 'failed',
      header: 'Passed / Failed',
      sortable: true,
      sortValue: (r) => toNumber(r.failed),
      render: (r) => (
        <span className="rpt-pf">
          <span className="rpt-text--success">{r.passed ?? 0}</span>
          <span className="rpt-muted">/</span>
          <span className={toNumber(r.failed) > 0 ? 'rpt-text--danger rpt-strong' : 'rpt-muted'}>{r.failed ?? 0}</span>
        </span>
      ),
    },
    { key: 'environment_name', header: 'Environment', sortable: true, render: (r) => r.environment_name || '—' },
    { key: 'executed_via', header: 'Via', render: (r) => r.executed_via || '—' },
    { key: 'duration', header: 'Duration', sortable: true, align: 'right', sortValue: (r) => toNumber(r.duration, null), render: (r) => <span className="rpt-mono">{formatDuration(r.duration)}</span> },
    { key: 'executed_at', header: 'Executed', sortable: true, render: (r) => formatDateTime(r.executed_at) },
  ];

  const planColumns = [
    { key: 'test_plan_name', header: 'Test plan', sortable: true, render: (r) => <span className="rpt-cell-title">{r.test_plan_name || '—'}</span> },
    { key: 'execution_status', header: 'Status', sortable: true, sortValue: (r) => normalizeStatus(r.execution_status), render: (r) => <StatusPill status={r.execution_status} /> },
    { key: 'suites', header: 'Suites', align: 'right', sortable: true, sortValue: (r) => (r.suites || []).length, render: (r) => (r.suites || []).length },
    { key: 'total_test_cases', header: 'Test cases', align: 'right', sortable: true, render: (r) => r.total_test_cases ?? 0 },
    { key: 'pass_rate', header: 'Pass rate', width: 160, sortable: true, sortValue: (r) => toNumber(r.pass_rate, null), render: (r) => <PassRateBar rate={r.pass_rate} compact /> },
    { key: 'duration', header: 'Duration', align: 'right', sortable: true, sortValue: (r) => toNumber(r.duration, null), render: (r) => <span className="rpt-mono">{formatDuration(r.duration)}</span> },
    { key: 'executed_at', header: 'Executed', sortable: true, render: (r) => formatDateTime(r.executed_at) },
  ];

  const lvl = (bucket, withPending) => (
    <span className="rpt-pf">
      <span className="rpt-text--success">{bucket?.passed ?? 0}</span>
      <span className="rpt-muted">/</span>
      <span className={toNumber(bucket?.failed) > 0 ? 'rpt-text--danger rpt-strong' : 'rpt-muted'}>{bucket?.failed ?? 0}</span>
      {withPending && toNumber(bucket?.pending) > 0 && (
        <>
          <span className="rpt-muted">/</span>
          <span className="rpt-text--warning">{bucket.pending}</span>
        </>
      )}
    </span>
  );

  const envColumns = [
    { key: 'environment_name', header: 'Environment', sortable: true, render: (r) => <span className="rpt-cell-title">{r.environment_name || '—'}</span> },
    { key: 'pass_rate', header: 'Pass rate', sortable: true, width: 160, sortValue: (r) => toNumber(r.pass_rate, null), render: (r) => <PassRateBar rate={r.pass_rate} compact /> },
    { key: 'plans', header: 'Plans P/F', render: (r) => lvl(r.test_plans) },
    { key: 'suites', header: 'Suites P/F', render: (r) => lvl(r.test_suites) },
    { key: 'cases', header: 'Cases P/F/Pend', sortable: true, sortValue: (r) => toNumber(r.test_cases?.failed), render: (r) => lvl(r.test_cases, true) },
    { key: 'coverage', header: 'Coverage', align: 'right', sortable: true, sortValue: (r) => toNumber(r.coverage, null), render: (r) => formatPercent(r.coverage) },
    { key: 'avg', header: 'Avg duration', align: 'right', sortable: true, sortValue: (r) => toNumber(r.average_execution_duration, null), render: (r) => <span className="rpt-mono">{formatDuration(r.average_execution_duration)}</span> },
    { key: 'status', header: 'Status', render: (r) => <HealthPill rate={r.pass_rate} /> },
  ];

  const tabCounts = {
    cases: cases.data?.total,
    runs: runs.data?.total,
    plans: plans.data?.total,
    environments: envs.data?.environments?.length,
  };

  const drawerHistory = drawer?.kind === 'case' ? h?.map?.get(testKeyOf(drawer.row)) : null;

  return (
    <div className="rpt-stack">
      <ReportSection title="Execution overview" subtitle="Results and coverage for the selected period">
        {execSummary.isLoading && <ReportSkeleton height={110} />}
        {execSummary.isError && <ReportErrorState compact onRetry={() => execSummary.refetch()} />}
        {execSummary.data && (
          <div className="rpt-overview">
            <div className="rpt-overview__score">
              <span className={`rpt-overview__rate rpt-text--${passRateTone(results?.pass_rate)}`}>
                {formatPercent(results?.pass_rate, results?.pass_rate % 1 ? 1 : 0)}
              </span>
              <span className="rpt-overview__caption">pass rate</span>
              {health?.status && <HealthPill rate={results?.pass_rate} label={health.status} />}
            </div>
            <div className="rpt-overview__outcome">
              <OutcomeBar
                segments={[
                  { key: 'passed', label: 'Passed', value: toNumber(results?.passed), tone: 'success' },
                  { key: 'failed', label: 'Failed', value: toNumber(results?.failed), tone: 'danger' },
                  { key: 'pending', label: 'Pending', value: toNumber(results?.pending), tone: 'warning' },
                ]}
                caption={health?.recommendation || undefined}
              />
            </div>
            <div className="rpt-coverage" aria-label="Execution coverage">
              <CoverageRow label="Test plans" bucket={coverage?.test_plans} />
              <CoverageRow label="Test suites" bucket={coverage?.test_suites} />
              <CoverageRow label="Test cases" bucket={coverage?.test_cases} />
            </div>
          </div>
        )}
      </ReportSection>

      <div className="rpt-grid rpt-grid--3-2">
        <ReportSection title="Execution duration trend" subtitle="Average test duration per day">
          {daily.isLoading && <ReportSkeleton height={200} />}
          {daily.isError && <ReportErrorState compact onRetry={() => daily.refetch()} />}
          {daily.data && !hasDuration && (
            <ReportEmptyState compact icon={<LuTimer size={20} />} title="No duration data" description="Durations appear once executions report timing." />
          )}
          {daily.data && hasDuration && (
            <Suspense fallback={<ReportSkeleton height={200} />}>
              <TrendChart
                points={durationPoints}
                tone="primary"
                height={200}
                formatValue={(v) => formatDuration(v)}
                ariaLabel="Average test duration per day"
              />
            </Suspense>
          )}
        </ReportSection>

        <ReportSection
          title="What changed"
          subtitle="Latest result of each test vs its earlier runs in this period"
        >
          {history.isLoading && <ReportSkeleton height={120} />}
          {history.isError && <ReportErrorState compact onRetry={() => history.refetch()} />}
          {h && <ChangeTiles history={h} onOpen={() => nav.toReport('failures')} />}
          {h?.truncated && (
            <p className="rpt-caption">
              Based on {h.rows.length.toLocaleString()} of {h.total.toLocaleString()} executions in this period.
            </p>
          )}
        </ReportSection>
      </div>

      <div className="rpt-grid rpt-grid--2">
        <ReportSection title="Slowest tests" subtitle="Highest average duration in this period">
          {history.isLoading && <ReportSkeleton variant="list" rows={5} />}
          {h && !h.slowest.length && <p className="rpt-empty-inline">No duration data in this period.</p>}
          {h && h.slowest.length > 0 && (
            <ol className="rpt-ranklist">
              {h.slowest.map((t, i) => (
                <li key={t.key}>
                  <button type="button" className="rpt-ranklist__row" onClick={() => openCase(historyToCaseRow(t))}>
                    <span className="rpt-ranklist__rank">{i + 1}</span>
                    <span className="rpt-ranklist__main">
                      <span className="rpt-ranklist__title">{t.name}</span>
                      <span className="rpt-ranklist__sub">
                        {t.suite || '—'} · {t.runCount} run{t.runCount !== 1 ? 's' : ''}
                        {t.maxDuration != null && ` · max ${formatDuration(t.maxDuration)}`}
                      </span>
                    </span>
                    <StatusPill status={t.latestStatus} />
                    <span className="rpt-ranklist__value rpt-mono">{formatDuration(t.avgDuration)}</span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </ReportSection>

        <ReportSection title="Results by suite" subtitle="Suites with the most failures first">
          {caseOverview.isLoading && <ReportSkeleton variant="list" rows={5} />}
          {caseOverview.isError && <ReportErrorState compact onRetry={() => caseOverview.refetch()} />}
          {caseOverview.data && <SuiteStatusBars rows={caseOverview.data.chart} />}
        </ReportSection>
      </div>

      <ReportSection title="Execution data" flush className="rpt-section--tabs">
        <div className="rpt-subtabs" role="tablist" aria-label="Execution data view">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={view === v.id}
              className={`rpt-subtabs__tab ${view === v.id ? 'is-active' : ''}`}
              onClick={() => update({ view: v.id }, { push: true })}
            >
              {v.label}
              {tabCounts[v.id] != null && <span className="rpt-subtabs__count">{tabCounts[v.id].toLocaleString()}</span>}
            </button>
          ))}
        </div>

        {view === 'cases' && (
          <>
            <div className="rpt-toolbar">
              <SearchInput value={caseQ} onChange={setCaseQ} placeholder="Search test cases…" className="rpt-search" />
              <Select size="sm" value={params.c_status} onChange={(e) => update({ c_status: e.target.value })} aria-label="Filter by status">
                <option value="all">All statuses</option>
                <option value="Passed">Passed</option>
                <option value="Failed">Failed</option>
              </Select>
            </div>
            <ReportTable
              caption="Test case executions"
              columns={caseColumns}
              rows={cases.data?.rows || []}
              rowKey={(r, i) => r.execution_id ?? `${r.test_case_id}-${r.executed_at}-${i}`}
              loading={cases.isLoading}
              fetching={cases.isFetching}
              error={cases.isError}
              onRetry={() => cases.refetch()}
              onRowClick={openCase}
              rowLabel={(r) => `View details for ${r.test_case_name}`}
              rowClassName={(r) => (normalizeStatus(r.status) === 'Failed' ? 'is-failed' : '')}
              sort={params.c_sort}
              onSortChange={(s) => update({ c_sort: s })}
              emptyTitle={params.c_q || params.c_status !== 'all' ? 'No test cases match these filters' : 'No test case executions in this period'}
              pagination={{
                page: params.c_page,
                pageSize: params.c_size,
                total: cases.data?.total || 0,
                onPageChange: (p) => update({ c_page: p }, { push: true }),
                onPageSizeChange: (n) => update({ c_size: n }),
                pageSizeOptions: [10, 20, 50, 100],
              }}
            />
          </>
        )}

        {view === 'runs' && (
          <>
            <div className="rpt-toolbar">
              <SearchInput value={runQ} onChange={setRunQ} placeholder="Search suites…" className="rpt-search" />
              <Select size="sm" value={params.r_type} onChange={(e) => update({ r_type: e.target.value })} aria-label="Filter by how the suite was executed">
                <option value="all">All executions</option>
                <option value="plan">Run via test plan</option>
                <option value="individual">Run individually</option>
              </Select>
              {runs.data?.summary && (
                <span className="rpt-toolbar__meta">
                  {toNumber(runs.data.summary.passed_executions)} passed · {toNumber(runs.data.summary.failed_executions)} failed of{' '}
                  {toNumber(runs.data.summary.suite_executions)} runs across {toNumber(runs.data.summary.project_suites)} suites
                </span>
              )}
            </div>
            <ReportTable
              caption="Suite executions"
              columns={runColumns}
              rows={runs.data?.rows || []}
              rowKey={(r, i) => r.suite_execution_id ?? i}
              loading={runs.isLoading}
              fetching={runs.isFetching}
              error={runs.isError}
              onRetry={() => runs.refetch()}
              onRowClick={(row) => setDrawer({ kind: 'suite', row })}
              rowLabel={(r) => `View run of ${r.suite_name}`}
              rowClassName={(r) => (toNumber(r.failed) > 0 ? 'has-failures' : '')}
              sort={params.r_sort}
              onSortChange={(s) => update({ r_sort: s })}
              emptyTitle={params.r_q || params.r_type !== 'all' ? 'No suite runs match these filters' : 'No suite runs in this period'}
              pagination={{
                page: params.r_page,
                pageSize: params.r_size,
                total: runs.data?.total || 0,
                onPageChange: (p) => update({ r_page: p }, { push: true }),
                onPageSizeChange: (n) => update({ r_size: n }),
              }}
            />
          </>
        )}

        {view === 'plans' && (
          <>
            <div className="rpt-toolbar">
              <SearchInput value={planQ} onChange={setPlanQ} placeholder="Search test plans…" className="rpt-search" />
            </div>
            <ReportTable
              caption="Test plan executions"
              columns={planColumns}
              rows={plans.data?.rows || []}
              rowKey={(r, i) => r.plan_execution_id ?? i}
              loading={plans.isLoading}
              fetching={plans.isFetching}
              error={plans.isError}
              onRetry={() => plans.refetch()}
              onRowClick={(row) => setDrawer({ kind: 'plan', row })}
              rowLabel={(r) => `View run of ${r.test_plan_name}`}
              rowClassName={(r) => (normalizeStatus(r.execution_status) === 'Failed' ? 'has-failures' : '')}
              sort={params.p_sort}
              onSortChange={(s) => update({ p_sort: s })}
              emptyTitle={params.p_q ? 'No test plans match your search' : 'No test plan runs in this period'}
              pagination={{
                page: params.p_page,
                pageSize: params.p_size,
                total: plans.data?.total || 0,
                onPageChange: (p) => update({ p_page: p }, { push: true }),
                onPageSizeChange: (n) => update({ p_size: n }),
              }}
            />
          </>
        )}

        {view === 'environments' && (
          <>
            {envs.data?.summary && (
              <div className="rpt-toolbar">
                <span className="rpt-toolbar__meta">
                  {toNumber(envs.data.summary.total_test_cases_executed)} cases executed across{' '}
                  {toNumber(envs.data.summary.total_environments)} environments · average pass rate{' '}
                  {formatPercent(envs.data.summary.average_pass_rate)} · average duration{' '}
                  {formatDuration(envs.data.summary.average_execution_duration)}
                </span>
              </div>
            )}
            <ReportTable
              caption="Environment breakdown"
              columns={envColumns}
              rows={envs.data?.environments || []}
              rowKey={(r, i) => r.environment_id ?? r.environment_name ?? i}
              loading={envs.isLoading}
              fetching={envs.isFetching}
              error={envs.isError}
              onRetry={() => envs.refetch()}
              rowClassName={(r) => (toNumber(r.test_cases?.failed) > 0 ? 'has-failures' : '')}
              sort={params.e_sort}
              onSortChange={(s) => update({ e_sort: s })}
              emptyTitle="No environment data in this period"
            />
          </>
        )}
      </ReportSection>

      <ReportDrawer
        open={Boolean(drawer)}
        onClose={closeDrawer}
        eyebrow={{ case: 'Test case execution', suite: 'Suite run', plan: 'Test plan run' }[drawer?.kind]}
        title={
          drawer?.kind === 'case'
            ? drawer.row.test_case_name || 'Test case'
            : drawer?.kind === 'suite'
              ? drawer.row.suite_name || 'Suite'
              : drawer?.row?.test_plan_name || 'Test plan'
        }
        footer={
          drawer?.kind === 'case' ? (
            <DrawerActions
              actions={[
                drawer.row.test_case_id != null && { label: 'Open test case', onClick: () => nav.openTestCase(drawer.row.test_case_id) },
                drawer.row.execution_id != null && { label: 'Open execution', primary: true, onClick: () => nav.openCaseExecution(drawer.row) },
              ]}
            />
          ) : null
        }
      >
        {drawer?.kind === 'case' && (
          <TestCaseDetails
            row={drawer.row}
            history={drawerHistory}
            historyLoading={history.isLoading}
            historyTruncated={h?.truncated}
          />
        )}
        {drawer?.kind === 'suite' && <SuiteRunDetails run={drawer.row} />}
        {drawer?.kind === 'plan' && <PlanRunDetails run={drawer.row} />}
      </ReportDrawer>

      <p className="rpt-caption rpt-caption--end">
        Need only the failures?{' '}
        <button type="button" className="rpt-link" onClick={() => nav.toReport('failures')}>
          Open the Failures report <LuArrowRight size={12} aria-hidden />
        </button>
      </p>
    </div>
  );
}
